import classNames from 'classnames'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { ActionsMenu } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/actionsMenu/ActionsMenu'
import { YEAR_WHEN_GP_APPEARS } from 'legacy/lut/features/globalPartner/constants'
import {
  selectCommitmentStatuses,
  selectCommitmentStatusesLoadingStatus,
} from 'legacy/lut/features/partner360/commitment'
import {
  changeOfferingsMode,
  changeProgramType,
  selectConfigurationLoadingStatus,
  selectFinancialYear,
  selectOfferingsMode,
  selectPartnerAccess,
  selectProgramType,
  selectProgramTypes,
} from 'legacy/lut/features/partner360/configuration'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { FC, useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { Nullable } from 'clickNgo/models/utils'
import { Translation } from 'cng-common/features/translations/components/translation'
import { deleteParamsFromUrl } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import MeasureController from 'core/analytics/MeasureController'
import { useNavigate } from 'core/hooks/useNavigate'
import { URLContext } from 'core/routes/urlController'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { TrainingCentre } from 'partner360/components/trainingCentre/TrainingCentre'
import { PLANNING_METRIC_URL_SEGMENT, PLANNING_SECTION_URL_SEGMENT } from 'partner360/configs'
import { CockpitHelpTools } from 'partner360/features/financialCockpit'

import { isUserHasRightsForElt } from '../ELT'
import { ModalProvider } from '../planningWizard/components/modal/context/ModalProvider'
import {
  closeWizard,
  openWizard,
  selectWizardIsVisible,
} from '../planningWizard/features/planningWizard'
import { PlanningWizard } from '../planningWizard/PlanningWizard'

import { ProgramTypeButtonsControl } from './components/programTypeButtonsControl/ProgramTypeButtonsControl'
import { ProgramTypesEditor } from './components/programTypesEditor/ProgramTypesEditor'
import { QuickStartButton } from './components/quickStartButton/QuickStartButton'

import styles from './ProgramTypeButtons.module.scss'

interface IProgramTypeButtonsProps {
  widgetParams: IObjectAny
  flexWidth: FlexDirectionBreak
  metrics: MeasureController
  pid?: string
  eltGroupUIID?: Nullable<string>
  className?: string
}

export const ProgramTypeButtons: FC<IProgramTypeButtonsProps> = (props) => {
  /** -------------------- Selectors ----------------------- */

  const isEditMode = useSelector(selectPublishingMode)
  const partnerAccess = useSelector(selectPartnerAccess)
  const user = useSelector(selectUser)
  const loadingStatus = useSelector(selectConfigurationLoadingStatus)
  const programType = useSelector(selectProgramType)
  const fYear = useSelector(selectFinancialYear)
  const isNewOfferings = useSelector(selectOfferingsMode)
  const { isGlobal } = useSelector(selectPartnerAccess)
  const programTypes = useSelector(selectProgramTypes)
  const commitmentStatuses = useSelector(selectCommitmentStatuses)
  const commitmentStatusesLoadingStatus = useSelector(selectCommitmentStatusesLoadingStatus)
  const isPlanningWizardVisible = useSelector(selectWizardIsVisible)

  /** -------------------- Hooks ----------------------- */
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { jbpSettings } = useJbpSettings()
  const { checkPendings } = useContext(URLContext)
  const { isPartnerUser } = useUserRoles()
  const partnerId = usePartnerId(props.widgetParams, props.pid, true)

  /** -------------------- Variables ----------------------- */

  const isQuickStartButtonDisplayed = useMemo(() => fYear < 2024, [fYear])

  const willRequestPartnerData = useMemo(() => {
    if (!partnerId || !fYear) {
      return false
    }
    return !(partnerId.startsWith('GP') && fYear < YEAR_WHEN_GP_APPEARS)
  }, [partnerId, fYear])

  const isCommitmentStatusesAreLoading = useMemo(
    () => commitmentStatusesLoadingStatus === RequestStatus.PENDING,
    [commitmentStatusesLoadingStatus]
  )

  const hasDeprecatedOfferings = useMemo(() => {
    return programTypes.some((it) => it.isDeprecated)
  }, [programTypes])

  const isUserHasRightsForQbr = useMemo(() => {
    return user.roleIds.some(
      (el) =>
        el === 'SuperAdmin' ||
        el === 'P360PartnerManager' ||
        el === 'P360SalesManager' ||
        el === 'P360GeoLead'
    )
  }, [user.roleIds])

  const isRetailer = useMemo(() => {
    return programType === ProgramTypes.Retail
  }, [programType])

  const isShowElt = useMemo(() => {
    return Boolean(
      (!isGlobal || props.eltGroupUIID) && !isRetailer && isUserHasRightsForElt(user.roleIds)
    )
  }, [isGlobal, isRetailer, props.eltGroupUIID, user.roleIds])

  const isShowQbr = useMemo(() => {
    return (partnerAccess.isCommercial || partnerAccess.isDistributor) && isUserHasRightsForQbr
  }, [isUserHasRightsForQbr, partnerAccess.isCommercial, partnerAccess.isDistributor])

  const isShowSobTrends = useMemo(() => {
    return !isPartnerUser && !isRetailer
  }, [isPartnerUser, isRetailer])

  const isShowPlanningWizard = useMemo(() => {
    return isRetailer
  }, [isRetailer])

  /** -------------------- User actions handlers ----------------------- */

  const handleClosePlanningWizard = useCallback(() => {
    dispatch(closeWizard())
  }, [dispatch])

  const handleProgramTypeChange = useCallback(
    async (programType: ProgramTypes) => {
      if (isEditMode) {
        const shouldDiscard = await checkPendings()
        if (!shouldDiscard) {
          return
        }
      }
      navigate(deleteParamsFromUrl([PLANNING_SECTION_URL_SEGMENT, PLANNING_METRIC_URL_SEGMENT]))
      dispatch(changeProgramType(programType))
    },
    [isEditMode, navigate, dispatch, checkPendings]
  )

  const handleChangeOfferingsMode = useCallback(
    (newMode: boolean) => {
      dispatch(changeOfferingsMode(newMode))
    },
    [dispatch]
  )

  /** -------------------- Render ----------------------- */

  if (
    loadingStatus === RequestStatus.PENDING ||
    (loadingStatus === RequestStatus.NONE && willRequestPartnerData)
  ) {
    return <Loader />
  }

  return (
    <div className={classNames(styles.root, props.className || '')}>
      <div className={styles.row} data-testid="programTypeButtons" data-help-panel-host>
        <div className={styles.controlGroup}>
          <p className={styles.label}>
            <Translation text="jbp-partner-program-types-title" actionsInside span />
          </p>

          <div className={styles.programTypesLine}>
            <ProgramTypeButtonsControl
              programTypes={programTypes}
              commitmentStatuses={commitmentStatuses}
              selectedProgramType={programType}
              onChange={handleProgramTypeChange}
              isStatusLoading={isCommitmentStatusesAreLoading}
              fYear={fYear}
              partnerId={partnerId}
            />

            {hasDeprecatedOfferings && (
              <div className={styles.offeringsSwitcher}>
                <Switch
                  className={styles.offeringsSwitcher__checkbox}
                  checked={isNewOfferings}
                  onChange={handleChangeOfferingsMode}
                />
                <div className={styles.offeringsSwitcher__label}>
                  <Translation span text="jbp-partner-type-switch" />
                </div>
              </div>
            )}
            <ProgramTypesEditor partnerId={partnerId} fYear={fYear} programTypes={programTypes} />

            <div className={styles.divider} />
            <ActionsMenu
              isShowElt={isShowElt}
              fYear={jbpSettings.fYear}
              programType={programType}
              isShowQbr={isShowQbr}
              isShowSobTrends={isShowSobTrends}
              partnerId={partnerId}
              eltGroupUIID={props.eltGroupUIID}
            />
          </div>
        </div>

        <div className={classNames(styles.actionsGroup, styles['row--push-right'])}>
          <RenderWhen condition={!isPartnerUser}>
            <TrainingCentre />
          </RenderWhen>

          <CockpitHelpTools partnerId={partnerId} mode="classic" />

          {isQuickStartButtonDisplayed && (
            <QuickStartButton link={jbpSettings.quickStartGuideLink} />
          )}
        </div>

        {isShowPlanningWizard && (
          <>
            <Button onClick={() => dispatch(openWizard())} disabled={isPlanningWizardVisible}>
              <Translation text="jbp-planning-wizard-button" span />
            </Button>

            <ModalProvider>
              <div className={styles.hpSobTracking}>
                <div className={styles.content} />

                <PlanningWizard
                  isOpen={isPlanningWizardVisible}
                  onClose={handleClosePlanningWizard}
                  widgetParams={props.widgetParams}
                  flexWidth={props.flexWidth}
                  metrics={props.metrics}
                  pid={props.pid || ''}
                />
              </div>
            </ModalProvider>
          </>
        )}
      </div>
    </div>
  )
}

ProgramTypeButtons.displayName = 'ProgramTypeButtons'
